import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@v2/components/ui/button";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { cn } from "@v2/lib/utils";
import { EmptyState } from "../../components/state";
import { calculateSquadScoreDetailed } from "../../lib/squad-score";
import type {
  MatchPlayerStat,
  Player,
  SessionResponse,
  SquadScoreSetting,
} from "../../types";

type Level = "low" | "med" | "high";
type PresetId = "balanced" | "strict" | "performance" | "consistency";

type MacroLevels = { presence: Level; performance: Level; consistency: Level };

const PRESENCE_TABLE: Record<Level, Pick<SquadScoreSetting,
  "training_present_points" | "match_day_present_points" | "late_penalty" | "absence_penalty" | "no_response_penalty"
>> = {
  low:  { training_present_points: 0.5, match_day_present_points: 1, late_penalty: 0,    absence_penalty: -0.5, no_response_penalty: -0.5 },
  med:  { training_present_points: 1,   match_day_present_points: 2, late_penalty: -0.5, absence_penalty: -1,   no_response_penalty: -1.5 },
  high: { training_present_points: 1.5, match_day_present_points: 3, late_penalty: -1,   absence_penalty: -2,   no_response_penalty: -3 },
};

const PERFORMANCE_TABLE: Record<Level, Pick<SquadScoreSetting,
  "goal_points" | "assist_points" | "yellow_card_penalty" | "red_card_penalty" | "win_bonus" | "draw_points" | "loss_penalty"
>> = {
  low:  { goal_points: 0.5, assist_points: 0.5,  yellow_card_penalty: 0,     red_card_penalty: -0.5, win_bonus: 0,    draw_points: 0,    loss_penalty: 0 },
  med:  { goal_points: 1,   assist_points: 0.75, yellow_card_penalty: -0.25, red_card_penalty: -1,   win_bonus: 0.25, draw_points: 0,    loss_penalty: -0.25 },
  high: { goal_points: 2,   assist_points: 1.5,  yellow_card_penalty: -0.5,  red_card_penalty: -2,   win_bonus: 0.75, draw_points: 0.25, loss_penalty: -0.5 },
};

const CONSISTENCY_TABLE: Record<Level, { min_events: number; decay_half_life_days: number | null }> = {
  low:  { min_events: 3, decay_half_life_days: null },
  med:  { min_events: 5, decay_half_life_days: null },
  high: { min_events: 8, decay_half_life_days: 90 },
};

const PRESET_MAP: Record<PresetId, MacroLevels> = {
  balanced:    { presence: "med",  performance: "med",  consistency: "med" },
  strict:      { presence: "high", performance: "med",  consistency: "high" },
  performance: { presence: "med",  performance: "high", consistency: "low" },
  consistency: { presence: "high", performance: "low",  consistency: "high" },
};

const LEVEL_ORDER: Level[] = ["low", "med", "high"];
const PRESET_ORDER: PresetId[] = ["balanced", "strict", "performance", "consistency"];

function applyLevels(base: SquadScoreSetting, levels: MacroLevels): SquadScoreSetting {
  return {
    ...base,
    ...PRESENCE_TABLE[levels.presence],
    ...PERFORMANCE_TABLE[levels.performance],
    ...CONSISTENCY_TABLE[levels.consistency],
  };
}

function detectLevel<T extends Record<string, number | null>>(setting: SquadScoreSetting, table: Record<Level, T>): Level | null {
  for (const level of LEVEL_ORDER) {
    const row = table[level];
    const match = Object.entries(row).every(([key, val]) => {
      const current = (setting as unknown as Record<string, number | null>)[key];
      if (val === null) return current === null;
      return Math.abs((current ?? 0) - (val as number)) < 1e-6;
    });
    if (match) return level;
  }
  return null;
}

function detectLevels(setting: SquadScoreSetting): MacroLevels | null {
  const presence = detectLevel(setting, PRESENCE_TABLE as unknown as Record<Level, Record<string, number | null>>);
  const performance = detectLevel(setting, PERFORMANCE_TABLE as unknown as Record<Level, Record<string, number | null>>);
  const consistency = detectLevel(setting, CONSISTENCY_TABLE as unknown as Record<Level, Record<string, number | null>>);
  if (!presence || !performance || !consistency) return null;
  return { presence, performance, consistency };
}

function detectPreset(setting: SquadScoreSetting): PresetId | null {
  const levels = detectLevels(setting);
  if (!levels) return null;
  for (const id of PRESET_ORDER) {
    const target = PRESET_MAP[id];
    if (
      target.presence === levels.presence &&
      target.performance === levels.performance &&
      target.consistency === levels.consistency
    ) {
      return id;
    }
  }
  return null;
}

export function SquadScoreSettingsForm({
  setting,
  isSaving,
  onSave,
}: {
  setting: SquadScoreSetting;
  isSaving: boolean;
  onSave: (setting: SquadScoreSetting) => void;
}) {
  const { t } = useTranslation("settings");
  const [draft, setDraft] = useState(setting);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    setDraft(setting);
  }, [setting]);

  const levels = useMemo(() => detectLevels(draft), [draft]);
  const activePreset = useMemo(() => detectPreset(draft), [draft]);

  const setLevel = (axis: keyof MacroLevels, level: Level) => {
    const current = levels ?? { presence: "med", performance: "med", consistency: "med" } as MacroLevels;
    const next = applyLevels(draft, { ...current, [axis]: level });
    setDraft(next);
  };

  const applyPreset = (id: PresetId) => {
    setDraft(applyLevels(draft, PRESET_MAP[id]));
  };

  const updateNumber = (key: keyof SquadScoreSetting, value: string) => {
    setDraft((current) => ({ ...current, [key]: Number(value || 0) }));
  };

  const updateDecay = (value: string) => {
    const num = Number(value || 0);
    setDraft((current) => ({ ...current, decay_half_life_days: num > 0 ? num : null }));
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(draft);
      }}
    >
      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs text-muted-foreground">
            {t("squadScore.presets.label")}
          </Label>
          {!activePreset && (
            <span className="rounded-full border bg-muted text-muted-foreground px-2 py-0.5 text-xs font-medium">
              {t("squadScore.presets.custom")}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {PRESET_ORDER.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => applyPreset(id)}
              className={cn(
                "rounded-2xl border px-3 py-2 text-sm font-medium transition-smooth text-center",
                activePreset === id
                  ? "border-transparent bg-primary text-primary-foreground"
                  : "bg-card hover:bg-muted/60 text-foreground/80",
              )}
            >
              {t(`squadScore.presets.${id}`)}
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <Label className="text-xs text-muted-foreground">
          {t("squadScore.macro.label")}
        </Label>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
          <MacroAxis
            title={t("squadScore.macro.presence.title")}
            description={t("squadScore.macro.presence.desc")}
            level={levels?.presence ?? null}
            onChange={(l) => setLevel("presence", l)}
            tLabel={(l) => t(`squadScore.levels.${l}`)}
          />
          <MacroAxis
            title={t("squadScore.macro.performance.title")}
            description={t("squadScore.macro.performance.desc")}
            level={levels?.performance ?? null}
            onChange={(l) => setLevel("performance", l)}
            tLabel={(l) => t(`squadScore.levels.${l}`)}
          />
          <MacroAxis
            title={t("squadScore.macro.consistency.title")}
            description={t("squadScore.macro.consistency.desc")}
            level={levels?.consistency ?? null}
            onChange={(l) => setLevel("consistency", l)}
            tLabel={(l) => t(`squadScore.levels.${l}`)}
          />
        </div>
      </section>

      <section>
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="w-full flex items-center justify-between rounded-2xl bg-background hover:bg-muted/60 px-3 py-2 text-sm font-medium transition-smooth"
        >
          <span>{t("squadScore.advanced.toggle")}</span>
          <span className={cn("text-muted-foreground transition-transform", advancedOpen ? "rotate-180" : "")}>
            ▾
          </span>
        </button>
        {advancedOpen && (
          <div className="mt-3 space-y-4 rounded-2xl bg-background px-3 py-3">
            <AdvancedGroup title={t("squadScore.advanced.presenceGroup")}>
              <NumField label={t("squadScore.labelTraining")} value={draft.training_present_points} onChange={(v) => updateNumber("training_present_points", v)} />
              <NumField label={t("squadScore.labelMatch")} value={draft.match_day_present_points} onChange={(v) => updateNumber("match_day_present_points", v)} />
              <NumField label={t("squadScore.labelLate")} value={draft.late_penalty} onChange={(v) => updateNumber("late_penalty", v)} />
              <NumField label={t("squadScore.labelAbsence")} value={draft.absence_penalty} onChange={(v) => updateNumber("absence_penalty", v)} />
              <NumField label={t("squadScore.labelNoResponse")} value={draft.no_response_penalty} onChange={(v) => updateNumber("no_response_penalty", v)} />
            </AdvancedGroup>
            <AdvancedGroup title={t("squadScore.advanced.performanceGroup")}>
              <NumField label={t("squadScore.labelGoal")} value={draft.goal_points} onChange={(v) => updateNumber("goal_points", v)} />
              <NumField label={t("squadScore.labelAssist")} value={draft.assist_points} onChange={(v) => updateNumber("assist_points", v)} />
              <NumField label={t("squadScore.labelYellow")} value={draft.yellow_card_penalty} onChange={(v) => updateNumber("yellow_card_penalty", v)} />
              <NumField label={t("squadScore.labelRed")} value={draft.red_card_penalty} onChange={(v) => updateNumber("red_card_penalty", v)} />
              <NumField label={t("squadScore.labelWin")} value={draft.win_bonus} onChange={(v) => updateNumber("win_bonus", v)} />
              <NumField label={t("squadScore.labelDraw")} value={draft.draw_points} onChange={(v) => updateNumber("draw_points", v)} />
              <NumField label={t("squadScore.labelLoss")} value={draft.loss_penalty} onChange={(v) => updateNumber("loss_penalty", v)} />
            </AdvancedGroup>
            <AdvancedGroup title={t("squadScore.advanced.consistencyGroup")}>
              <NumField label={t("squadScore.labelMinEvents")} value={draft.min_events} step="1" onChange={(v) => updateNumber("min_events", v)} />
              <div className="space-y-1.5">
                <Label className="text-xs">{t("squadScore.labelDecay")}</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  placeholder={t("squadScore.decayPlaceholder")}
                  value={draft.decay_half_life_days ?? ""}
                  onChange={(e) => updateDecay(e.target.value)}
                />
                <p className="text-xs text-muted-foreground leading-tight">{t("squadScore.decayHelp")}</p>
              </div>
            </AdvancedGroup>
          </div>
        )}
      </section>

      <div className="flex flex-col sm:flex-row sm:items-end gap-3 pt-1 border-t pt-4">
        <div className="flex-1 min-w-0 space-y-1.5">
          <Label className="text-xs">{t("squadScore.presetName")}</Label>
          <Input value={draft.name} onChange={(e) => setDraft((c) => ({ ...c, name: e.target.value }))} />
        </div>
        <Button type="submit" disabled={isSaving} className="sm:w-auto w-full">
          {isSaving ? t("squadScore.saving") : t("squadScore.saveWeights")}
        </Button>
      </div>
    </form>
  );
}

function AdvancedGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1.5">{title}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{children}</div>
    </div>
  );
}

function NumField({
  label,
  value,
  onChange,
  step = "any",
}: {
  label: string;
  value: number;
  onChange: (value: string) => void;
  step?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function MacroAxis({
  title,
  description,
  level,
  onChange,
  tLabel,
}: {
  title: string;
  description: string;
  level: Level | null;
  onChange: (level: Level) => void;
  tLabel: (level: Level) => string;
}) {
  return (
    <div className="rounded-2xl bg-background p-3 flex flex-col gap-3">
      <div className="space-y-1">
        <p className="text-sm font-medium leading-tight">{title}</p>
        <p className="text-xs text-muted-foreground leading-snug min-h-[2.25rem]">{description}</p>
      </div>
      <div className="inline-flex w-full rounded-lg bg-muted/60 p-0.5">
        {LEVEL_ORDER.map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => onChange(l)}
            className={cn(
              "flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors",
              level === l
                ? "bg-primary text-primary-foreground "
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {tLabel(l)}
          </button>
        ))}
      </div>
    </div>
  );
}

export function SquadScorePreview({
  players,
  responses,
  matchStats,
  setting,
}: {
  players: Player[];
  responses: SessionResponse[];
  matchStats: MatchPlayerStat[];
  setting: SquadScoreSetting;
}) {
  const { t } = useTranslation("settings");
  const rows = players
    .filter((p) => !p.is_guest)
    .map((player) => {
      const playerResponses = responses.filter((r) => r.player_id === player.id);
      const playerStats = matchStats.filter((s) => s.player_id === player.id);
      const breakdown = calculateSquadScoreDetailed(playerResponses, playerStats, setting);
      return {
        player,
        score: breakdown.score,
        avg: breakdown.avgPerEvent,
        events: playerResponses.length,
        goals: playerStats.reduce((acc, s) => acc + (s.goals || 0), 0),
        assists: playerStats.reduce((acc, s) => acc + (s.assists || 0), 0),
      };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 12);

  if (rows.length === 0) return <EmptyState text={t("squadScore.previewEmpty")} />;

  return (
    <div className="space-y-1.5 max-h-[28rem] overflow-y-auto pr-1">
      {rows.map((row, idx) => (
        <div key={row.player.id} className="flex items-center justify-between rounded-2xl bg-background px-3 py-2">
          <div className="flex items-center gap-3 min-w-0">
            <span className={cn(
              "h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold",
              idx === 0 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            )}>
              {idx + 1}
            </span>
            <div className="min-w-0">
              <p className="font-medium truncate">{row.player.display_name}</p>
              <p className="text-xs text-muted-foreground">
                {t("squadScore.previewEvents", { events: row.events, goals: row.goals, assists: row.assists })}
              </p>
            </div>
          </div>
          <div className="text-right">
            <span className="font-bold tabular-nums text-base">{row.score.toFixed(1)}</span>
            {row.events > 0 && (
              <p className="text-xs text-muted-foreground tabular-nums">
                {t("squadScore.avgPerEvent", { value: row.avg.toFixed(2) })}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
